// What this chat has put on the canvas, for the header's Back/Forward and its "In this chat" list. Pure state:
// the hook (use-canvas-navigation) feeds it every canvas that arrives, the header reads it.
//
// Two shapes over the same arrivals:
//   - `items`: each thing once, newest first (the switcher list). Transient views are not things.
//   - `stack` + `cursor`: the order they were visited in, so Back and Forward move through them like a browser does;
//     opening something new from the middle drops what was ahead.
import { sameCanvasObject } from './canvas-navigation';
import type { CanvasTarget } from './types';

/** Views that are not things the chat made or opened: they never enter the list or the stack. */
// About this project is the home's own panel, not a thing the chat opened.
const TRANSIENT = new Set<CanvasTarget['kind']>([
  'activity',
  'choices',
  'action',
  'voice-preview',
  'project',
]);
export const isTransientCanvas = (target: CanvasTarget | null | undefined) =>
  !target || TRANSIENT.has(target.kind);

export type CanvasLogItem = {
  /** The canvas that opens it (the last tab/scope it was on, never full-screen state). */
  target: CanvasTarget;
  /** Its own name and kind label, once its canvas has loaded and said so. */
  name: string | null;
  kind: string | null;
  /** Its canvas reported that it can no longer be opened. */
  unavailable: boolean;
  /** When it was last opened (ms). */
  at: number;
};

export type CanvasLog = {
  items: CanvasLogItem[];
  stack: CanvasLogItem['target'][];
  cursor: number;
};

export const emptyCanvasLog = (): CanvasLog => ({ items: [], stack: [], cursor: -1 });

// Where it was shown (full screen) and whether it was pinned are how it was opened, not what it is.
const strip = (target: CanvasTarget): CanvasTarget => {
  const { pinned: _pinned, ...rest } = target as CanvasTarget & { pinned?: boolean };
  return { ...rest, full: undefined } as CanvasTarget;
};
const same = (a: CanvasTarget, b: CanvasTarget) => sameCanvasObject(a, b);

/**
 * A canvas arrived. `via` says how: an ordinary open (it joins the stack at the cursor) or an arrow (the stack was
 * already walked to it, only the list's recency changes).
 */
export function recordCanvas(
  log: CanvasLog,
  target: CanvasTarget | null,
  now: number,
  via: 'open' | 'arrow' = 'open',
): CanvasLog {
  if (!target || isTransientCanvas(target)) return log;
  const clean = strip(target);
  const existing = log.items.find((item) => same(item.target, clean));
  const item: CanvasLogItem = {
    target: clean,
    name: existing?.name ?? null,
    kind: existing?.kind ?? null,
    unavailable: existing?.unavailable ?? false,
    at: now,
  };
  const items = [item, ...log.items.filter((other) => other !== existing)];
  if (via === 'arrow') {
    const at = log.stack.findIndex((entry) => same(entry, clean));
    return { items, stack: log.stack, cursor: at >= 0 ? at : log.cursor };
  }
  const current = log.stack[log.cursor];
  // The same object on show (a tab or scope change): this entry updates, nothing new is visited.
  if (current && same(current, clean)) {
    const stack = log.stack.slice();
    stack[log.cursor] = clean;
    return { items, stack, cursor: log.cursor };
  }
  const stack = [...log.stack.slice(0, log.cursor + 1), clean];
  return { items, stack, cursor: stack.length - 1 };
}

/** A loaded canvas names itself (or says it cannot be opened). */
export function noteCanvas(
  log: CanvasLog,
  target: CanvasTarget,
  meta: { name?: string | null; kind?: string | null; unavailable?: boolean },
): CanvasLog {
  const at = log.items.findIndex((item) => same(item.target, target));
  if (at < 0) return log;
  const old = log.items[at];
  const next = {
    ...old,
    name: meta.name ?? old.name,
    kind: meta.kind ?? old.kind,
    unavailable: meta.unavailable ?? old.unavailable,
  };
  if (next.name === old.name && next.kind === old.kind && next.unavailable === old.unavailable)
    return log;
  const items = log.items.slice();
  items[at] = next;
  return { ...log, items };
}

const itemFor = (log: CanvasLog, target: CanvasTarget) =>
  log.items.find((item) => same(item.target, target)) ?? null;

/** The nearest openable entry before / after the cursor (an item that is gone is skipped, never attempted). */
export function neighbour(log: CanvasLog, direction: -1 | 1) {
  for (let at = log.cursor + direction; at >= 0 && at < log.stack.length; at += direction) {
    const item = itemFor(log, log.stack[at]);
    if (item && !item.unavailable) return { index: at, item };
  }
  return null;
}

/** Walk the cursor to a stack index (an arrow was used). */
export const moveCursor = (log: CanvasLog, index: number): CanvasLog => ({ ...log, cursor: index });

/** The switcher's rows are shown only once the chat has opened two or more things. */
export const hasHistory = (log: CanvasLog) => log.items.length >= 2;
